import { useEffect, useState, type RefObject } from 'react';

/**
 * Whether `ref`'s content runs past its own box (taller than it shows).
 *
 * Measured, not guessed from character counts: a short text with blank lines
 * is tall, and a fixed-height box clips it all the same. Re-measured whenever
 * the box resizes, and whenever `key` changes (new content in the same box).
 */
export function useIsClipped(ref: RefObject<HTMLElement | null>, key: unknown): boolean {
  const [clipped, setClipped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // One pixel of slack: sub-pixel line heights round scrollHeight up.
    const measure = () => setClipped(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, key]);

  return clipped;
}
